import { defineMeta } from '@/registry/types';

export const analystMarkdownMeta = defineMeta({
  name: 'AnalystMarkdown',
  level: 'organism',
  category: 'data-display',
  classification: 'custom',
  description:
    'Renders an AI-written investigation summary, turning resolved entity values inside the prose into interactive chips an analyst can copy and pivot on. Ships from the @aistrike-dev/ui/markdown subpath so the markdown renderer stays out of the root bundle.',
  baseLibrary: 'react-markdown',
  importPath: '@aistrike-dev/ui/markdown',
  requiredProps: ['source'],
  optionalProps: [
    'entities',
    'aiGenerated',
    'maxMarkedPerBlock',
    'entityActions',
    'onEntityAction',
  ],
  supportedStates: ['default', 'ai-generated', 'entity-menu-open', 'no-entities'],
  designTokens: ['typography', 'spacing', 'colors'],
  accessibility: [
    'Entity chips join the tab order in reading order, and each opens its menu with Enter or Space.',
    'Every entity has an accessible name carrying its kind, its full canonical value and its verdict, since the visible text may be truncated and the tint is not perceivable to everyone.',
    'A long summary is a lot of tab stops. Keep the marked-span cap low and treat a chip-dense paragraph as a content problem upstream rather than a navigation problem to work around.',
  ],
  usageExamples: [
    "import { AnalystMarkdown } from '@aistrike-dev/ui/markdown';",
    '<AnalystMarkdown source={summary.markdown} entities={summary.entities} aiGenerated />',
    '<AnalystMarkdown source={md} entities={spans} entityActions={[{ id: "pivot", label: "Pivot to search" }]} onEntityAction={run} />',
  ],
  rules: [
    'Import from `@aistrike-dev/ui/markdown`, never from the root entry; the root stays free of the markdown renderer.',
    'Supply `entities` as offsets into the exact `source` string you pass, produced by the model or the enrichment service.',
    'Mark only what an analyst can act on: network and file indicators, principals, and catalogue references. Not ordinary nouns, product names, times or counts.',
    'Pass the canonical full value as `value` and any shortened form as `display`, so a truncated hash still copies in full.',
    'Set `aiGenerated` for model-written content. One marker covers the block.',
    'Keep `maxMarkedPerBlock` at or below the default of 8. A summary genuinely dense with indicators wants a table underneath it, not more chips inside it.',
    'Put pivot and lookup actions in `entityActions`; keep response actions on the entity page.',
  ],
  antiPatterns: [
    'Do not derive entity spans by pattern-matching the prose. A pattern for IP addresses also matches a version number, and a hash pattern matches any long hex string; every false positive attaches a menu to something with no entity behind it.',
    'Do not attach a verdict to a value that was not resolved; that is a fabricated claim.',
    'Do not compute offsets against rendered text. Emphasis markers, links and escapes shift the indices and silently attach entities to the wrong words.',
    'Do not add rehype-raw or any raw-HTML plugin. The input is model output, and parsing HTML in it would make this component an XSS sink.',
    'Do not put an AI marker on every chip; mark the generated block once.',
    'Do not use this for prose the product wrote itself; plain Typography is lighter and needs no renderer.',
  ],
  llmSafe: true,
  whenToUse: [
    'AI-generated investigation or alert summaries where analysts need to act on the values',
    'Any model output whose entities have already been resolved to structured spans',
  ],
  whenNotToUse: [
    'Static product copy (use Typography)',
    'Untrusted markdown with no entity resolution (there is nothing to gain over Typography)',
    'Code display (use CodeEditor from @aistrike-dev/ui/monaco)',
  ],
});

export default analystMarkdownMeta;
